import { Reveal, MaskedLine, Overline } from "@/components/Reveal";

const PRINCIPLES = [
  { n: "01", title: "Bespoke or nothing", body: "No templates, no page builders, no shortcuts. Every product is engineered from a blank canvas around your goals." },
  { n: "02", title: "Performance is design", body: "Speed, accessibility and SEO aren't tickets in a backlog — they're designed into the first sketch." },
  { n: "03", title: "Launch is the start", body: "We stay after go-live, measuring, iterating and amplifying until the numbers move." },
  { n: "04", title: "AI with intent", body: "We champion AI where it compounds value — AI Warehouse, our own marketing platform, exists to promote the best AI products and services." },
];

const STACK = ["React", "React Native", "Node.js", "Python", "FastAPI", "MongoDB", "AI Infrastructure", "Cloud Native"];

const About = () => (
  <main data-testid="about-page" className="pt-20">
    <section className="relative py-24 lg:py-36 overflow-hidden grid-bg">
      <div className="absolute -top-32 right-0 w-[28rem] h-[28rem] bg-cyan-400/10 blur-[140px] rounded-full" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <MaskedLine delay={0.1}>
          <Overline>About Us</Overline>
        </MaskedLine>
        <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl mt-6">
          <MaskedLine delay={0.25}>Engineers of</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-stroke">digital distinction</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6} className="max-w-2xl">
          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed mt-8">
            Market Rounds Technologies is a forward-thinking IT company dedicated to
            delivering innovative solutions in web development, app development and
            digital marketing. Our expert team blends creativity with technical
            excellence to provide customised solutions that help businesses thrive
            in the digital world.
          </p>
        </MaskedLine>
      </div>
    </section>

    <section className="py-24 lg:py-32 bg-white text-black" data-testid="about-story">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Overline className="text-cyan-600">Our Story</Overline>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl lg:text-4xl mt-4 leading-tight">
            From Nottingham, for the world
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="space-y-6 text-zinc-600 text-sm sm:text-base leading-relaxed">
          <p>
            Based at the NBV Enterprise Centre in Nottingham, we started with a simple
            conviction: businesses deserve technology partners who care about outcomes,
            not just deliverables.
          </p>
          <p>
            Today we design and engineer for clients across energy, property
            and artificial intelligence — and we ship our own products too, including{" "}
            <a
              href="https://ai-warehouse.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-600 font-medium underline decoration-cyan-400/40 underline-offset-4 hover:decoration-cyan-500 transition-colors"
            >
              AI Warehouse
            </a>
            , our marketing platform that promotes AI products and services
            to the businesses searching for them.
          </p>
          <p>
            Every engagement is a partnership: senior people, honest timelines, and work
            we're proud to sign.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-24 lg:py-32 bg-[#050507] border-y border-zinc-800/80" data-testid="about-principles">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Overline>How We Work</Overline>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl lg:text-4xl mt-4">
            Core principles
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 mt-14 border-t border-l border-zinc-800">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.08}>
              <div className="group h-full border-b border-r border-zinc-800 p-8 lg:p-10 hover:bg-black transition-colors duration-500">
                <span className="font-mono-accent text-cyan-400 text-sm">{p.n}/</span>
                <h3 className="font-display font-bold uppercase text-xl mt-5 group-hover:text-cyan-400 transition-colors">
                  {p.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed mt-3">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 lg:py-32 bg-white text-black" data-testid="about-studio">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Overline className="text-cyan-600">The Studio</Overline>
          <h2 className="font-display tracking-tight text-3xl lg:text-4xl mt-4 max-w-2xl leading-tight">
            One senior team. No handoffs — experienced hands on every line.
          </h2>
          <p className="text-zinc-600 text-sm sm:text-base leading-relaxed mt-6 max-w-2xl">
            Market Rounds is a tight collective of designers, engineers and growth
            strategists. The people you meet on day one are the people who design,
            build and ship your product — start to finish.
          </p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 rounded-2xl overflow-hidden mt-14">
          {[
            { n: "25+", l: "Projects shipped", d: "Across energy, property, food, media and AI" },
            { n: "3", l: "Core disciplines", d: "Web, mobile and digital marketing under one roof" },
            { n: "100%", l: "Senior-led delivery", d: "Every line reviewed by a lead engineer" },
            { n: "1", l: "HQ in Nottingham", d: "UK-based, working with clients worldwide" },
          ].map((s, i) => (
            <Reveal key={s.l} delay={i * 0.08}>
              <div data-testid={`studio-stat-${i}`} className="group bg-white p-8 h-full hover:bg-zinc-50 transition-colors duration-500">
                <span className="font-display text-5xl text-black group-hover:text-cyan-600 transition-colors">
                  {s.n}
                </span>
                <h3 className="font-semibold text-sm mt-4">{s.l}</h3>
                <p className="text-zinc-600 text-sm leading-relaxed mt-2">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-24 lg:py-32 bg-[#050507] border-t border-zinc-800/80" data-testid="about-stack">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Overline>The Arsenal</Overline>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl lg:text-4xl mt-4">
            Tech we trust
          </h2>
        </Reveal>
        <div className="flex flex-wrap gap-3 mt-12">
          {STACK.map((tech, i) => (
            <Reveal key={tech} delay={i * 0.05}>
              <span className="inline-block border border-zinc-700 px-5 py-3 font-mono-accent text-xs uppercase tracking-[0.2em] text-zinc-300 hover:border-cyan-400 hover:text-cyan-400 transition-colors cursor-default">
                {tech}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </main>
);

export default About;
